import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useFinance } from '@/contexts/FinanceContext';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import type { FinanceCategory, TransactionType } from '@/types/finance';

const COLORS = [
  '#16a34a',
  '#2563eb',
  '#dc2626',
  '#d97706',
  '#7c3aed',
  '#0891b2',
  '#db2777',
  '#65a30d',
  '#ea580c',
  '#4f46e5',
  '#113554',
  '#f37b53',
];

interface CategoryFormProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editCategory?: FinanceCategory;
}

function CategoryModal({ open, onOpenChange, editCategory }: CategoryFormProps) {
  const { addCategory, updateCategory } = useFinance();
  const [name, setName] = useState('');
  const [type, setType] = useState<TransactionType>('expense');
  const [color, setColor] = useState(COLORS[0]!);

  // Sincroniza state com editCategory toda vez que o modal abre.
  // useState só roda na primeira render — sem isso, ao abrir "editar" depois
  // de já ter aberto "criar", o nome aparece em branco.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    if (editCategory) {
      setName(editCategory.name);
      setType(editCategory.type);
      setColor(editCategory.color);
    } else {
      setName('');
      setType('expense');
      setColor(COLORS[0]!);
    }
  }, [open, editCategory]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (editCategory) {
      updateCategory(editCategory.id, { name, type, color });
    } else {
      addCategory({ name, type, color, icon: 'Circle' });
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="font-brand">
            {editCategory ? 'Editar Categoria' : 'Nova Categoria'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="mt-2 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cat-name">Nome *</Label>
            <Input
              id="cat-name"
              placeholder="Ex: Materiais médicos"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label>Tipo</Label>
            <Select
              value={type}
              onValueChange={(v) => setType(v as TransactionType)}
              disabled={!!editCategory}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="income">Receita</SelectItem>
                <SelectItem value="expense">Despesa</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Cor</Label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`h-8 w-8 rounded-full border-2 transition-all ${color === c ? 'scale-110 border-foreground' : 'border-transparent hover:scale-105'}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">{editCategory ? 'Salvar' : 'Criar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CategoriesPage() {
  const { clientId } = useCurrentClient();
  const { categories, deleteCategory, isLoading } = useFinance();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FinanceCategory | undefined>();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  if (!clientId) {
    return (
      <div className="rounded-xl border border-border bg-white p-8 text-center text-muted-foreground">
        <p className="text-sm">Selecione um cliente para gerenciar as categorias.</p>
      </div>
    );
  }

  const incomeCategories = categories.filter((c) => c.type === 'income');
  const expenseCategories = categories.filter((c) => c.type === 'expense');

  const openCreate = () => {
    setEditingCategory(undefined);
    setModalOpen(true);
  };
  const openEdit = (c: FinanceCategory) => {
    setEditingCategory(c);
    setModalOpen(true);
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">Categorias</h1>
          <p className="text-sm text-muted-foreground">
            {categories.length} categoria{categories.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
        >
          <Plus size={16} className="mr-2" />
          Nova Categoria
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="h-32 animate-pulse rounded-xl border bg-muted" />
          ))}
        </div>
      ) : categories.length === 0 ? (
        <div className="rounded-xl border border-border bg-white p-12 text-center text-muted-foreground">
          <Tag className="mx-auto mb-3 h-12 w-12 opacity-30" />
          <p className="font-medium">Nenhuma categoria cadastrada</p>
          <p className="mt-1 text-sm">Crie categorias para organizar as transações.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {[
            {
              title: 'Receitas',
              items: incomeCategories,
              color: 'text-income',
              bg: 'bg-income/5 border-income/20',
            },
            {
              title: 'Despesas',
              items: expenseCategories,
              color: 'text-expense',
              bg: 'bg-expense/5 border-expense/20',
            },
          ].map(
            ({ title, items, color, bg }) =>
              items.length > 0 && (
                <div key={title}>
                  <h2 className={`mb-3 text-sm font-semibold ${color}`}>{title}</h2>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((c) => (
                      <div
                        key={c.id}
                        className={`flex items-center justify-between rounded-lg border p-3 ${bg}`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="h-4 w-4 rounded-full"
                            style={{ backgroundColor: c.color }}
                          />
                          <span className="text-sm font-medium">{c.name}</span>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => openEdit(c)}
                          >
                            <Pencil size={13} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => setDeletingId(c.id)}
                          >
                            <Trash2 size={13} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ),
          )}
        </div>
      )}

      <CategoryModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        {...(editingCategory ? { editCategory: editingCategory } : {})}
      />

      <AlertDialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir categoria?</AlertDialogTitle>
            <AlertDialogDescription>
              As transações vinculadas ficarão sem categoria.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingId) {
                  deleteCategory(deletingId);
                  setDeletingId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
